import { URGENCY_BUCKETS } from "../constants";

export function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatRelative(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffD = Math.round(diffHr / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return formatDate(value);
}

export function isSameLocalDay(a, b) {
  const d1 = new Date(a);
  const d2 = new Date(b);
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function hoursSince(value) {
  if (!value) return Infinity;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return Infinity;
  return (Date.now() - d.getTime()) / 3_600_000;
}

// Map an inquiry to its urgency bucket id. Returns null when replied/closed.
export function urgencyFor(inquiry) {
  if (!inquiry || inquiry.closed || inquiry.replied) return null;
  const h = hoursSince(inquiry.createdAt);
  for (const bucket of URGENCY_BUCKETS) {
    if (h >= bucket.minHours) return bucket.id;
  }
  return "new";
}

export function urgencyTone(id) {
  const bucket = URGENCY_BUCKETS.find((b) => b.id === id);
  if (!bucket) return { dot: "bg-zinc-300", ring: "ring-zinc-200" };
  switch (bucket.tone) {
    case "red":
      return {
        dot: "bg-red-500",
        bar: "bg-red-500",
        chip: "bg-red-50 text-red-700 ring-red-200",
      };
    case "amber":
      return {
        dot: "bg-amber-500",
        bar: "bg-amber-500",
        chip: "bg-amber-50 text-amber-700 ring-amber-200",
      };
    case "indigo":
      return {
        dot: "bg-indigo-500",
        bar: "bg-indigo-500",
        chip: "bg-indigo-50 text-indigo-700 ring-indigo-200",
      };
    case "emerald":
    default:
      return {
        dot: "bg-emerald-500",
        bar: "bg-emerald-500",
        chip: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      };
  }
}

export function statusOf(inquiry) {
  if (!inquiry) return "new";
  if (inquiry.closed) return "closed";
  if (inquiry.replied) return "replied";
  return "new";
}

// Best-effort: first-response time is the gap between createdAt and the
// earliest response.respondedAt. Returns minutes, or null if never replied.
export function firstResponseMinutes(inquiry) {
  if (!inquiry?.responses?.length) return null;
  const first = inquiry.responses
    .map((r) => (r?.respondedAt ? new Date(r.respondedAt).getTime() : null))
    .filter(Boolean)
    .sort((a, b) => a - b)[0];
  if (!first) return null;
  const created = new Date(inquiry.createdAt).getTime();
  if (!created || first < created) return null;
  return Math.round((first - created) / 60000);
}

export function formatDuration(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) {
    const whole = Math.floor(hours);
    const mins = Math.round(minutes - whole * 60);
    return mins ? `${whole}h ${mins}m` : `${whole}h`;
  }
  const days = hours / 24;
  const wholeD = Math.floor(days);
  const remHours = Math.round(hours - wholeD * 24);
  return remHours ? `${wholeD}d ${remHours}h` : `${wholeD}d`;
}

// --------- Merge vars scoped to inquiries -----------------------------------
// We re-use the same {{var|fallback}} convention as leads so operators don't
// have to learn a new syntax. The canonical implementation lives in the leads
// workspace utils; importing from there keeps behavior aligned.

export {
  renderTemplate,
  detectUnresolvedVars,
  listMergeVars,
} from "../../leadsComponents/workspace/utils";

const splitName = (full) => {
  if (!full) return { first: "", last: "" };
  const parts = String(full).trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
};

export function buildInquiryMergeVars({ inquiry, admin } = {}) {
  const { first, last } = splitName(inquiry?.name || "");
  return {
    first_name: first,
    last_name: last,
    full_name: inquiry?.name || "",
    email: inquiry?.email || "",
    phone: inquiry?.phone || "",
    type: inquiry?.type || "",
    inquiry_subject: inquiry?.subject || "",
    inquiry_message: inquiry?.message || "",
    responder_first_name: admin?.firstName || "",
    responder_last_name: admin?.lastName || "",
    responder_full_name: [admin?.firstName, admin?.lastName]
      .filter(Boolean)
      .join(" "),
  };
}

// --------- Analytics -------------------------------------------------------

export function computeAnalytics(inquiries = []) {
  const now = Date.now();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const sevenDaysAgo = now - 7 * 86400000;
  const thirtyDaysAgo = now - 30 * 86400000;

  const buckets = {
    new: 0,
    fresh: 0,
    stale: 0,
    overdue: 0,
  };

  const byType = {};
  const byStatus = { new: 0, replied: 0, closed: 0 };
  const byResponder = new Map();
  let newToday = 0;
  let newYesterday = 0;
  let repliesLast7d = 0;
  let totalFirstResponseMin = 0;
  let firstResponseCount = 0;
  let oldestUnreplied = null;

  for (const inq of inquiries) {
    const type = inq?.type || "Other";
    byType[type] = (byType[type] || 0) + 1;

    const status = statusOf(inq);
    byStatus[status] = (byStatus[status] || 0) + 1;

    const createdMs = inq?.createdAt ? new Date(inq.createdAt).getTime() : 0;
    if (createdMs >= startOfToday.getTime()) newToday += 1;
    else if (
      createdMs >= startOfYesterday.getTime() &&
      createdMs < startOfToday.getTime()
    )
      newYesterday += 1;

    if (!inq.closed && !inq.replied) {
      const bucket = urgencyFor(inq);
      if (bucket && buckets[bucket] !== undefined) buckets[bucket] += 1;
      if (!oldestUnreplied || createdMs < new Date(oldestUnreplied.createdAt).getTime()) {
        oldestUnreplied = inq;
      }
    }

    const responses = Array.isArray(inq?.responses) ? inq.responses : [];
    for (const r of responses) {
      const respMs = r?.respondedAt ? new Date(r.respondedAt).getTime() : 0;
      if (respMs >= sevenDaysAgo) repliesLast7d += 1;
      if (r?.responder && respMs >= thirtyDaysAgo) {
        byResponder.set(r.responder, (byResponder.get(r.responder) || 0) + 1);
      }
    }

    const frm = firstResponseMinutes(inq);
    if (frm != null && createdMs >= thirtyDaysAgo) {
      totalFirstResponseMin += frm;
      firstResponseCount += 1;
    }
  }

  const avgFirstResponse = firstResponseCount
    ? totalFirstResponseMin / firstResponseCount
    : null;

  const totalOpen = (byStatus.new || 0) + (byStatus.replied || 0);
  const openPct = inquiries.length
    ? Math.round((totalOpen / inquiries.length) * 100)
    : 0;

  const responderList = Array.from(byResponder.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  return {
    total: inquiries.length,
    totalOpen,
    openPct,
    byStatus,
    byType,
    buckets,
    newToday,
    newYesterday,
    newTodayDelta: newToday - newYesterday,
    repliesLast7d,
    avgFirstResponseMinutes: avgFirstResponse,
    oldestUnreplied,
    byResponder: responderList,
  };
}

// Map inquiry -> sortable status rank. Lower ranks float to the top.
// `new` (awaiting a human) first, then `replied`, then `closed`.
function statusRank(i) {
  if (!i) return 3;
  if (i.closed) return 2;
  if (i.replied) return 1;
  return 0;
}

// Best-available "last activity" timestamp.
export function lastActivityMs(i) {
  if (!i) return 0;
  const candidates = [
    i.updatedAt,
    i.respondedAt,
    ...((i.responses || []).map((r) => r?.respondedAt)),
    ...((i.messages || []).map((m) => m?.timestamp)),
    i.createdAt,
  ]
    .map((v) => (v ? new Date(v).getTime() : 0))
    .filter((n) => Number.isFinite(n) && n > 0);
  return candidates.length ? Math.max(...candidates) : 0;
}

export const INQUIRY_SORT_OPTIONS = [
  { id: "newest", label: "Newest first" },
  { id: "oldest", label: "Oldest first" },
  { id: "updated", label: "Recently updated" },
  { id: "status", label: "Status" },
];

export function applyView({ inquiries, view, search, typeFilter, sortBy }) {
  const q = (search || "").trim().toLowerCase();
  const wantType = typeFilter && typeFilter !== "all" ? typeFilter : null;
  const filtered = (inquiries || []).filter((i) => {
    if (wantType && (i?.type || "Other") !== wantType) return false;
    if (q) {
      const hay = [
        i?.name,
        i?.email,
        i?.phone,
        i?.subject,
        i?.message,
        i?.type,
        ...(i?.responses || []).map((r) => r?.message),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    switch (view) {
      case "today":
        return (
          !i.closed && i.createdAt && isSameLocalDay(i.createdAt, new Date())
        );
      case "awaiting":
        return !i.replied && !i.closed;
      case "replied":
        return i.replied && !i.closed;
      case "closed":
        return i.closed;
      case "all":
      default:
        return true;
    }
  });

  // Effective sort: user's explicit choice wins; otherwise fall back to the
  // view-appropriate default (Awaiting -> oldest first so the most urgent
  // sits on top; everything else -> newest first).
  const effective =
    sortBy || (view === "awaiting" ? "oldest" : "newest");

  const byCreatedAsc = (a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  const byCreatedDesc = (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const sorted = filtered.slice();
  switch (effective) {
    case "oldest":
      sorted.sort(byCreatedAsc);
      break;
    case "updated":
      sorted.sort((a, b) => lastActivityMs(b) - lastActivityMs(a));
      break;
    case "status":
      sorted.sort((a, b) => {
        const r = statusRank(a) - statusRank(b);
        if (r !== 0) return r;
        return byCreatedDesc(a, b);
      });
      break;
    case "newest":
    default:
      sorted.sort(byCreatedDesc);
      break;
  }
  return sorted;
}
